import { ref, nextTick, type Ref } from "vue";
import { generateApolloClient } from "@/common/apolloClientUtil";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetUserId,
  localStorageGetShopCode,
} from "@/common/localStorageUtil";
import {
  DeleteTransPickupRequestDocument,
  type DeleteTransPickupRequestMutation,
  type DeleteTransPickupRequestMutationVariables,
  GetDeliveryRequestDocument,
  type GetDeliveryRequestQuery,
  type GetDeliveryRequestQueryVariables,
  type OperationInput,
  type TransPickupRequestUpdateInput,
  ListShopForTransPickupRequestDocument,
  type ListShopForTransPickupRequestQuery,
  type ShopListForTransPickupRequestInput,
} from "@/generated/graphql";
import type { ApolloQueryResult } from "@apollo/client/core";
import type {
  DeliveryAvailabilityData,
  DeliveryStartMinit,
  BeforData,
  ListData,
  DeliveryAreaSelectItem,
  ShopData,
} from "../types";
import { DeliveryRequireMoney } from "@/common/constant";

interface UseDeliveryMoveActionsParams {
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  editPage: Ref<boolean>;
  confirmPage: Ref<boolean>;
  processFlag: Ref<number>;
  confirmDeleteDialog: Ref<boolean>;
  overlay: Ref<boolean>;
  editDeliveryRequest: Ref<TransPickupRequestUpdateInput | null>;
  shopSelectValues_from: Ref<{ shop_code: string; name: string }>;
  shopSelectValues_to: Ref<{ shop_code: string; name: string }>;
  shopData_from_address: Ref<string>;
  shopData_to_address: Ref<string>;
  shopFromListItems: Ref<({ __typename?: "ShopResponse"; shop_code: string; name: string } | null)[]>;
  shopFromListInput: Ref<ShopListForTransPickupRequestInput>;
  isDisabled: Ref<boolean>;
  isDisabledChangeFromTo: Ref<boolean>;
  dummy_payment_amount: Ref<string>;
  deliveryNotesBefore: Ref<string>;
  messageDeliveryNotes: Ref<string>;
  deliveryAvailabilityData: Ref<DeliveryAvailabilityData[][]>;
  deliveryAvailabilityHeader: Ref<Map<string, string>>;
  disp_deliveryAvailabilityData: Ref<DeliveryAvailabilityData[][]>;
  deliveryAreaName: Ref<string>;
  display_delivery_time: Ref<string>;
  deliveryId_0_Flg: Ref<boolean>;
  deliveryStartTime: Ref<string>;
  deliveryEndTime: Ref<string>;
  deliveryStartHourItem: Ref<number[]>;
  deliveryStartHourValues: Ref<number | null>;
  deliveryStartMinitItem: Ref<DeliveryStartMinit[]>;
  deliveryStartMinitValues: Ref<DeliveryStartMinit | null | undefined>;
  befor_data: BeforData;
  irregularDeliveryStartTime: Ref<string>;
  irregularDeliveryEndTime: Ref<string>;
  acceptableEndTime: Ref<string>;
  listDeliveryArea: Ref<(DeliveryAreaSelectItem | null | undefined)[] | null | undefined>;
  isDoubleDeli: Ref<boolean>;
  isCharterDeli: Ref<boolean>;
  pre_double_deli_oricon_flg: Ref<number | null>;
  pre_charter_deli_oricon_flg: Ref<number | null>;
  selectListCheckbox: Ref<ListData[]>;
  filteredSelectListCheckbox: Ref<ListData[]>;
  paymentList: Ref<any>;
  paymentRequiredFlg: Ref<boolean>;
  chainViewFlag: Ref<boolean>;
  onClickSearch: (initail_mode?: boolean) => Promise<void>;
  openListPage: () => void;
  openConfirmPage: () => void;
  onChangeShopFrom: () => Promise<void>;
  getAreaList: (address: string, area_code?: string) => Promise<void>;
  onClickListDeliveryArea: (item: any) => Promise<void>;
  setDeliveryStartTime: () => Promise<void>;
  setDeliveryEndTime: () => Promise<void>;
}

const normalizeTimeParts = (time?: string | null): [string, string] => {
  const [hour = "00", minute = "00"] = (time ?? "").split(":");
  return [hour.padStart(2, "0"), minute.padStart(2, "0")];
};

/**
 * 画面の操作イベントに関するハンドラをまとめて提供する composable。
 *
 * @returns 画面イベントから呼び出す操作ハンドラ群
 */
export const useDeliveryMoveActions = ({
  errdialog,
  errMessage,
  editPage,
  confirmPage,
  processFlag,
  confirmDeleteDialog,
  overlay,
  editDeliveryRequest,
  shopSelectValues_from,
  shopSelectValues_to,
  shopData_from_address,
  shopData_to_address,
  shopFromListItems,
  shopFromListInput,
  isDisabled,
  isDisabledChangeFromTo,
  dummy_payment_amount,
  deliveryNotesBefore,
  messageDeliveryNotes,
  deliveryAvailabilityData,
  deliveryAvailabilityHeader,
  disp_deliveryAvailabilityData,
  deliveryAreaName,
  display_delivery_time,
  deliveryId_0_Flg,
  deliveryStartTime,
  deliveryEndTime,
  deliveryStartHourItem,
  deliveryStartHourValues,
  deliveryStartMinitItem,
  deliveryStartMinitValues,
  befor_data,
  irregularDeliveryStartTime,
  irregularDeliveryEndTime,
  acceptableEndTime,
  listDeliveryArea,
  isDoubleDeli,
  isCharterDeli,
  pre_double_deli_oricon_flg,
  pre_charter_deli_oricon_flg,
  selectListCheckbox,
  filteredSelectListCheckbox,
  paymentList,
  paymentRequiredFlg,
  chainViewFlag,
  onClickSearch,
  openListPage,
  openConfirmPage,
  onChangeShopFrom,
  getAreaList,
  onClickListDeliveryArea,
  setDeliveryStartTime,
  setDeliveryEndTime,
}: UseDeliveryMoveActionsParams) => {
  /**
   * 削除処理GraphQL実行(1件ずつ)
   */
  const deleteExecute = async (reception_no: string): Promise<boolean> => {
    // graphql接続
    const apolloClient = await generateApolloClient();
    // 削除ログ入力
    const logInput: OperationInput = {
      area_code: "10",
      detail: "操作詳細",
      display_category: "画面名",
      operation: "操作",
      user_id: localStorageGetUserId(),
    };

    try {
      const result = await apolloClient.mutate<
        DeleteTransPickupRequestMutation,
        DeleteTransPickupRequestMutationVariables
      >({
        mutation: DeleteTransPickupRequestDocument,
        variables: { logInput, reception_no },
      });
      if (!result.data || !result.data.deleteTransPickupRequest) {
        console.error("deleteTransPickupRequest error", result);
        return false;
      }
      return true;
    } catch (err: unknown) {
      if (err instanceof Error) console.error(err.message);
      else console.error(err);
      return false;
    }
  };

  /**
   * 配達依頼編集アイコン @Click
   */
  const onClickEdit = async (receptionNo: string, editAllowed: number): Promise<void> => {
    // 処理フラグ
    processFlag.value = 1;

    // 前回の編集セッションの警告フラグをリセット
    isDoubleDeli.value = false;
    isCharterDeli.value = false;

    // graphql接続
    const apolloClient = await generateApolloClient();
    // 配達依頼取得
    const logInput: OperationInput = {
      area_code: "10",
      detail: "操作詳細",
      display_category: "画面名",
      operation: "操作",
      user_id: localStorageGetUserId(),
    };

    let result: ApolloQueryResult<GetDeliveryRequestQuery>;
    try {
      result = await apolloClient.query<GetDeliveryRequestQuery, GetDeliveryRequestQueryVariables>({
        query: GetDeliveryRequestDocument,
        variables: {
          customer_code: localStorageGetCustomerCode(),
          chain_code: localStorageGetChainCode(),
          logInput,
          reception_no: receptionNo,
        },
      });
    } catch (err) {
      console.error("err=", err);
      errdialog.value = true;
      errMessage.value = "在庫移動・回収一覧データの取得に失敗しました。";
      // 編集対象 在庫移動・回収
      editDeliveryRequest.value = null;
      return;
    }

    if (!result.data.getDeliveryRequest) {
      console.error("result.data.getDeliveryRequest is undefined or null");
      errdialog.value = true;
      errMessage.value = "在庫移動・回収一覧データの取得に失敗しました。";

      // 編集対象 在庫移動・回収
      editDeliveryRequest.value = null;
      return;
    }

    /**
     * 店舗データ取得処理
     */
    shopFromListInput.value.from_shop_code = localStorageGetShopCode();
    const listShop: ApolloQueryResult<ListShopForTransPickupRequestQuery> =
      await apolloClient.query<ListShopForTransPickupRequestQuery>({
        query: ListShopForTransPickupRequestDocument,
        variables: {
          input: shopFromListInput.value,
        },
      });

    shopFromListItems.value = listShop.data.listShopForTransPickupRequest;

    // 配達元住所初期表示
    shopData_from_address.value =
      "〒" +
      result.data.getDeliveryRequest.ship_from_zip_code +
      result.data.getDeliveryRequest.ship_from_address1! +
      result.data.getDeliveryRequest.ship_from_address2 +
      result.data.getDeliveryRequest.ship_from_address3;

    // 配達元名初期表示（読み取り専用オブジェクト対策で新しいオブジェクトを代入）
    shopSelectValues_from.value = {
      shop_code: result.data.getDeliveryRequest.shop_code,
      name: result.data.getDeliveryRequest.ship_from_name,
    };
    await onChangeShopFrom();

    // 配達先住所初期表示
    shopData_to_address.value =
      "〒" +
      result.data.getDeliveryRequest.delivery_zip_code +
      result.data.getDeliveryRequest.delivery_address1! +
      result.data.getDeliveryRequest.delivery_address2 +
      result.data.getDeliveryRequest.delivery_address3;
    // 配達先名初期表示（読み取り専用オブジェクト対策で新しいオブジェクトを代入）
    shopSelectValues_to.value = {
      shop_code: result.data.getDeliveryRequest.delivery_shop_code || "",
      name: result.data.getDeliveryRequest.delivery_name!,
    };
    isDisabledChangeFromTo.value = false;

    // 支払金額表示用
    if (result.data.getDeliveryRequest.payment_amount !== null) {
      dummy_payment_amount.value = String(
        result.data.getDeliveryRequest.payment_amount
      ).replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1,");
    } else {
      dummy_payment_amount.value = "";
    }
    // 編集対象 配達依頼（コピーを作成して読み取り専用エラーを回避）
    const deliveryRequestCopy = { ...result.data.getDeliveryRequest };
    // 配達希望時間帯の時間部分を取り除く
    deliveryRequestCopy.required_delivery_date =
      deliveryRequestCopy.required_delivery_date!.substr(0, 10);

    editDeliveryRequest.value = deliveryRequestCopy as unknown as TransPickupRequestUpdateInput;

    // 配達エリア取得
    await getAreaList(
      editDeliveryRequest.value.delivery_address1!,
      editDeliveryRequest.value.area_code
    );
    for (let i = 0; i < listDeliveryArea.value!.length; i++) {
      if (
        listDeliveryArea.value![i]!.area_code ==
        editDeliveryRequest.value.area_code
      ) {
        await onClickListDeliveryArea(listDeliveryArea.value![i]!);
      }
    }

    const requiedDate = editDeliveryRequest.value.required_delivery_date
      ? editDeliveryRequest.value.required_delivery_date.substr(0, 10)
      : "";

    let strDeliveryId = "";
    deliveryId_0_Flg.value = false;
    deliveryStartHourItem.value = [];
    deliveryStartHourValues.value = undefined;
    deliveryStartMinitValues.value = undefined;

    // 便の配達開始時間取得
    const nowDate = new Date();
    const today =
      `${nowDate.getFullYear()}-${String(nowDate.getMonth() + 1).padStart(2, "0")}-${String(nowDate.getDate()).padStart(2, "0")}`;
    const checkDate = requiedDate && requiedDate < today ? today : requiedDate;
    const matchedAvailability = deliveryAvailabilityData.value.find((value) => {
      const head = value?.[0];
      return !!head && head.delivery_date === checkDate && head.delivery_id === editDeliveryRequest.value!.delivery_id;
    });
    let deliveryStartTimeAry = normalizeTimeParts(
      matchedAvailability?.[0]?.delivery_start_time || editDeliveryRequest.value.irregular_delivery_start_time
    );
    let deliveryEndTimeAry = normalizeTimeParts(
      matchedAvailability?.[0]?.delivery_end_time ||
      editDeliveryRequest.value.irregular_delivery_end_time ||
      acceptableEndTime.value
    );

    editDeliveryRequest.value.unit = result.data.getDeliveryRequest.unit;

    if (editDeliveryRequest.value.delivery_id === 0) {
      // 都度便の場合、配送開始時間候補を計算
      strDeliveryId = "都度便";
      deliveryId_0_Flg.value = true;

      // 表示用時刻にパッドを付けてセット
      deliveryStartTime.value = `${deliveryStartTimeAry[0]}:${deliveryStartTimeAry[1]}`;
      deliveryEndTime.value = `${deliveryEndTimeAry[0]}:${deliveryEndTimeAry[1]}`;

      // 配達開始時間セットロジック（元のクラス版から移植）
      let startHour = 0;
      const now = new Date();
      const nowDate =
        `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      const endTimeAry = normalizeTimeParts(acceptableEndTime.value);
      if (
        requiedDate !== nowDate ||
        (requiedDate === nowDate &&
          (Number(deliveryStartTimeAry[0]) > now.getHours() ||
            (Number(deliveryStartTimeAry[0]) === now.getHours() &&
              Number(deliveryStartTimeAry[1]) > now.getMinutes())))
      ) {
        startHour = Number(deliveryStartTimeAry[0]);
      } else {
        startHour = now.getMinutes() < 30 ? now.getHours() : now.getHours() + 1;
      }
      // 時間候補をクリアしてから再設定
      deliveryStartHourItem.value = [];
      for (let i = startHour; i <= Number(endTimeAry[0]); i++) {
        deliveryStartHourItem.value.push(i);
      }

      deliveryStartMinitItem.value = [
        { label: "00", deliveryStartMinit: 0 },
        { label: "30", deliveryStartMinit: 1 },
      ];

      // 変更前データを保存
      befor_data.delivery_date = editDeliveryRequest.value.required_delivery_date;
      befor_data.delivery_id = editDeliveryRequest.value.delivery_id;
      befor_data.irregularDeliveryStartTime = normalizeTimeParts(
        editDeliveryRequest.value.irregular_delivery_start_time
      );

      if (
        Number(befor_data.irregularDeliveryStartTime[0]) < startHour
      ) {
        deliveryStartHourItem.value.unshift(
          Number(befor_data.irregularDeliveryStartTime[0])
        );
      }
      deliveryStartHourValues.value = Number(
        befor_data.irregularDeliveryStartTime[0]
      );
      if (befor_data.irregularDeliveryStartTime[1] === "30") {
        deliveryStartMinitItem.value.shift();
      }
      deliveryStartMinitItem.value.forEach((value) => {
        if (value.label === befor_data.irregularDeliveryStartTime[1]) {
          deliveryStartMinitValues.value = {
            label: value.label,
            deliveryStartMinit: value.deliveryStartMinit,
          };
        }
      });

      // 編集中データの irregular_delivery_start_time に合わせて再設定
      deliveryStartTimeAry = normalizeTimeParts(
        editDeliveryRequest.value.irregular_delivery_start_time
      );
      deliveryStartHourValues.value = Number(deliveryStartTimeAry[0]);
      deliveryStartMinitItem.value.forEach((value) => {
        if (value.label === deliveryStartTimeAry[1]) {
          deliveryStartMinitValues.value = {
            label: value.label,
            deliveryStartMinit: value.deliveryStartMinit,
          };
        }
      });

      //配達開始時間セット
      await setDeliveryStartTime();

      //配達終了時間セット
      await setDeliveryEndTime();
    } else {
      strDeliveryId = editDeliveryRequest.value.delivery_id + "便";
    }

    pre_double_deli_oricon_flg.value =
      editDeliveryRequest.value.double_deli_oricon_flg;
    pre_charter_deli_oricon_flg.value =
      editDeliveryRequest.value.charter_deli_oricon_flg;

    // 画面表示用
    display_delivery_time.value =
      editDeliveryRequest.value.required_delivery_date + " " + strDeliveryId;
    // 金額必須判定
    await onChangePaymentMethodHandler(editDeliveryRequest.value.payment_method);

    // 編集許可（画面表示切替） 0:編集不可、1:編集可、2:金額のみ編集可
    if (editAllowed === 2) {
      isDisabled.value = true;
    } else {
      isDisabled.value = false;
      isDisabledChangeFromTo.value = false;
    }

    //編集ページ表示
    editPage.value = true;
    window.scrollTo(0, 0);
  };

  /**
   * 一括削除 @Click
   */
  const onClickCheckDelete = async (): Promise<void> => {
    if (filteredSelectListCheckbox.value.length === 0) {
      return;
    }
    confirmDeleteDialog.value = true;
  };

  /**
   * 一括削除 実行
   */
  const onClickCheckDeleteComplete = async (): Promise<void> => {
    overlay.value = true;

    //1件ずつ削除する
    let result = false;
    let arrayError: string[] = [];
    for (const val of filteredSelectListCheckbox.value) {
      if (await deleteExecute(val.receptionNo)) {
        result = true;
      } else {
        arrayError.push(val.receptionNo);
      }
    }

    //画面を閉じる
    confirmDeleteDialog.value = false;
    overlay.value = false;

    //画面遷移
    if (result) {
      await nextTick(async () => {
        //削除
        selectListCheckbox.value = [];
        // 絞り込み検索
        await onClickSearch(true);
      });
    } else {
      errMessage.value =
        "受付番号 " + arrayError.join(",") + "の削除に失敗しました。";
      errdialog.value = true;
    }
  };

  /**
   * 配達依頼削除アイコン @Click
   */
  const onClickDelete = async (receptionNo: string): Promise<void> => {
    //処理フラグ
    processFlag.value = 0;

    // 前回の編集セッションの警告フラグをリセット
    isDoubleDeli.value = false;
    isCharterDeli.value = false;

    //graphql接続
    const apolloClient = await generateApolloClient();
    // 配達依頼取得
    const logInput: OperationInput = {
      area_code: "10",
      detail: "操作詳細",
      display_category: "画面名",
      operation: "操作",
      user_id: localStorageGetUserId(),
    };

    let result: ApolloQueryResult<GetDeliveryRequestQuery>;
    try {
      result = await apolloClient.query<GetDeliveryRequestQuery, GetDeliveryRequestQueryVariables>({
        query: GetDeliveryRequestDocument,
        variables: {
          customer_code: localStorageGetCustomerCode(),
          chain_code: localStorageGetChainCode(),
          logInput,
          reception_no: receptionNo,
        },
      });
    } catch (err) {
      console.error("err=", err);

      // 削除対象 配達依頼
      editDeliveryRequest.value = null;

      return;
    }

    if (!result.data.getDeliveryRequest) {
      console.error("result.data.getDeliveryRequest is undefined or null");

      // 削除対象 配達依頼
      editDeliveryRequest.value = null;

      return;
    }

    // 削除対象 配達依頼
    editDeliveryRequest.value =
      result.data.getDeliveryRequest as unknown as TransPickupRequestUpdateInput;
    // 配達元住所初期表示
    shopData_from_address.value =
      result.data.getDeliveryRequest.delivery_address1! +
      result.data.getDeliveryRequest.delivery_address2 +
      result.data.getDeliveryRequest.delivery_address3;
    // 配達元名設定
    shopSelectValues_from.value.shop_code = result.data.getDeliveryRequest.shop_code;
    shopSelectValues_from.value.name = result.data.getDeliveryRequest.ship_from_name;
    // 配達先名設定
    shopSelectValues_to.value.shop_code =
      result.data.getDeliveryRequest.delivery_shop_code || "";
    shopSelectValues_to.value.name = result.data.getDeliveryRequest.delivery_name!;
    // 配達エリア名設定
    deliveryAreaName.value = result.data.getDeliveryRequest.area_name;
    // 配達希望時間選択設定
    const deliveryIdValue = result.data.getDeliveryRequest.delivery_id;
    const strDeliveryId = deliveryIdValue === 0 ? "都度便" : deliveryIdValue + "便";
    const requiredDate = result.data.getDeliveryRequest.required_delivery_date
      ? result.data.getDeliveryRequest.required_delivery_date.substr(0, 10)
      : "";
    display_delivery_time.value = requiredDate + " " + strDeliveryId;
    // 都度便フラグ設定
    deliveryId_0_Flg.value = deliveryIdValue === 0;
    // 都度便の場合、不規則配達時間を設定
    if (deliveryId_0_Flg.value) {
      irregularDeliveryStartTime.value =
        result.data.getDeliveryRequest.irregular_delivery_start_time || "";
      irregularDeliveryEndTime.value =
        result.data.getDeliveryRequest.irregular_delivery_end_time || "";
    }
    // 金額が必須かどうか設定する
    await onChangePaymentMethodHandler(editDeliveryRequest.value.payment_method);
    dummy_payment_amount.value =
      editDeliveryRequest.value.payment_amount !== null
        ? String(editDeliveryRequest.value.payment_amount).replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1,")
        : "";

    // 確認ページを表示
    openConfirmPage();
  };

  /**
   * 配達依頼削除確認OK @Click
   */
  const onClickConfirmDeleteOk = async (): Promise<void> => {
    overlay.value = true;
    if (await deleteExecute(editDeliveryRequest.value!.reception_no)) {
      // 削除対象 配達依頼
      editDeliveryRequest.value = null;

      // 一覧表示に戻す
      openListPage();

      await nextTick(async () => {
        // 一覧反映のため一時待機
        await new Promise(resolve => setTimeout(resolve, 800));
        // 絞り込み再検索（ページを維持するためinitail_mode=trueで呼び出し）
        await onClickSearch(true);
      });
    } else {
      errdialog.value = true;
      errMessage.value = "削除処理に失敗しました。";
    }
  };

  /**
   * 配達依頼編集 キャンセル @Click
   */
  const onClickUpdateCancel = async (): Promise<void> => {
    // 編集対象 配達依頼
    editDeliveryRequest.value = null;
    // 編集ページ表示フラグ
    editPage.value = false;
    // 配達備考のダブルクォーテーションを戻す。
    if (deliveryNotesBefore.value !== "") {
      editDeliveryRequest.value!.delivery_notes = deliveryNotesBefore.value;
      deliveryNotesBefore.value = "";
      messageDeliveryNotes.value = "";
    }
  };

  /**
   * 決済方法によって支払金額必須か判定 @change
   */
  const onChangePaymentMethodHandler = async (
    item: string | undefined | null
  ): Promise<void> => {
    try {
      if (!item) {
        return;
      }
      const _paymentList = paymentList.value?.find(
        (element: any) => element?.payment_method === item
      );

      if (!_paymentList) {
        return;
      }

      if (
        _paymentList.payment_amount_required ===
        DeliveryRequireMoney.money_required
      ) {
        paymentRequiredFlg.value = true;
      } else {
        paymentRequiredFlg.value = false;
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        console.error(err);
        errMessage.value = err.message;
      } else {
        console.error("未知のエラー:", err);
        errMessage.value = "支払方法の取得に失敗しました。";
      }
      errdialog.value = true;
      return;
    }
  };

  return {
    deleteExecute,
    onClickEdit,
    onClickCheckDelete,
    onClickCheckDeleteComplete,
    onClickDelete,
    onClickConfirmDeleteOk,
    onClickUpdateCancel,
    onChangePaymentMethod: onChangePaymentMethodHandler,
  };
};
